import type { ProcessTemplateTaskType, TaskTemplateType } from '@econtractor/types';
import {
    getProcessNextTasks,
    getProcessPosition,
    pruneProcessNextTasks,
    remapProcessNextTaskInstance,
} from '../processDiagram/helpers/nextTask';

export const allocateProcessTaskDtId = (tasks: Pick<ProcessTemplateTaskType, 'dtId'>[]): number => {
    let dtId = Date.now();
    const used = new Set(tasks.map((task) => task.dtId));
    while (used.has(dtId)) dtId += 1;
    return dtId;
};

export const snapshotFromTaskTemplate = (
    template: TaskTemplateType,
    tasks: ProcessTemplateTaskType[],
    owned: boolean,
): ProcessTemplateTaskType => ({
    dtId: allocateProcessTaskDtId(tasks),
    templateId: template.ID,
    owned,
    taskType: template.taskType,
    taskName: template.taskName,
    taskDescription: template.taskDescription || '',
    taskExecutor: template.taskExecutor,
    taskController: template.taskController,
    reserveExecutorTime: Boolean(template.reserveExecutorTime),
    executorCanChangeTerm: Boolean(template.executorCanChangeTerm),
    noEarlyStart: Boolean(template.noEarlyStart),
    pay: Boolean(template.pay),
    payAmount: template.payAmount ?? null,
    payUsd: template.payUsd !== false,
    showPayToExecutor: Boolean(template.showPayToExecutor),
    categoryId: template.categoryId,
    categoryName: template.categoryName ?? null,
    taskTypeName: template.taskTypeName ?? null,
    taskTypeI18nKey: template.taskTypeI18nKey ?? null,
    taskTypeIcon: template.taskTypeIcon ?? null,
    taslTypeJson: template.taslTypeJson ?? null,
    executorUser: template.executorUser ?? null,
    controllerUser: template.controllerUser ?? null,
    answers: template.answers || [],
    termDays: template.termDays ?? null,
    processData: null,
});

export const appendProcessTask = (
    tasks: ProcessTemplateTaskType[],
    template: TaskTemplateType,
    owned: boolean,
): ProcessTemplateTaskType[] => [...tasks, snapshotFromTaskTemplate(template, tasks, owned)];

export const replaceProcessTask = (
    tasks: ProcessTemplateTaskType[],
    dtId: number,
    template: TaskTemplateType,
): ProcessTemplateTaskType[] => {
    const current = tasks.find((task) => task.dtId === dtId);
    if (!current) return tasks;
    const answers = template.answers || [];
    const keys = new Set(answers.map((answer) => answer.key));
    const nextTask = getProcessNextTasks(current.processData).filter(
        (link) => link.answerKey == null || keys.has(link.answerKey),
    );
    const position = getProcessPosition(current.processData);
    const replaced = tasks.map((task) => {
        if (task.dtId !== dtId) return task;
        return {
            ...snapshotFromTaskTemplate(template, [], true),
            dtId: task.dtId,
            owned: true,
            dateEnd: task.dateEnd ?? null,
            dateStart: task.dateStart ?? null,
            withTime: task.withTime,
            multiDay: task.multiDay,
            instanceId: task.instanceId ?? null,
            processData: !nextTask.length && !position ? null : position ? { nextTask, position } : { nextTask },
        };
    });
    return remapProcessNextTaskInstance(replaced, current, {
        templateId: template.ID,
        dtId,
    });
};

export const removeProcessTask = (
    tasks: ProcessTemplateTaskType[],
    removed: ProcessTemplateTaskType,
): ProcessTemplateTaskType[] =>
    pruneProcessNextTasks(
        tasks.filter((task) => task.dtId !== removed.dtId),
        removed,
    );
